import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { fetchStories, fetchPairs, MODEL_LABELS } from "../../lib/newsApi";
import { safeUrl } from "../../lib/safeUrl";
import { timeAgo } from "../../lib/tone";
import { StoryBody } from "./StoryDetail";
import { LocalActionCTA } from "./LocalActionCTA";
import { ShareMenu } from "../ShareMenu";

const COVERAGE = [["both", "Covered by both sides"], ["mainstream_only", "Only mainstream"], ["independent_only", "Only independent"]];

const StoryRow = ({ s }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid={`story-row-${s.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 p-4 text-left" aria-expanded={open} data-testid={`story-toggle-${s.id}`}>
        <span className="font-mono text-xs text-slate-400">{s.size}</span>
        <span className="flex-1">
          <span className="font-medium text-slate-900">{s.label}</span>
          <span className="mt-1 block text-xs text-slate-500">{s.sides.mainstream} mainstream · {s.sides.independent} independent · {s.source_ids.length} outlets · updated {timeAgo(s.updated)}</span>
        </span>
        <span className="hidden h-2 w-24 overflow-hidden rounded-full bg-emerald-200 sm:block" title="Mainstream vs independent share">
          <span className="block h-full bg-sky-700" style={{ width: `${(100 * s.sides.mainstream) / s.size}%` }} />
        </span>
        <ChevronDown size={18} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-slate-100 p-4"><StoryBody story={s} /><div className="mt-4 flex items-start gap-3"><div className="flex-1"><LocalActionCTA topics={s.topics} /></div><ShareMenu path={`/news?story=${s.id}`} title={s.label} kind="story" testId={`compare-share-${s.id}`} /></div></div>}
    </div>
  );
};

const PairCard = ({ p }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`pair-card-${p.mainstream.id}-${p.independent.id}`}>
    <div className="grid grid-cols-2 gap-4">
      {[["mainstream", p.mainstream], ["independent", p.independent]].map(([side, s]) => (
        <div key={side} className="min-w-0">
          <p className={`text-[10px] font-semibold uppercase tracking-wider ${side === "independent" ? "text-emerald-700" : "text-sky-700"}`}>{side}</p>
          <p className="font-heading font-semibold text-slate-900">{s.name}</p>
          <p className="text-[11px] text-slate-500">{MODEL_LABELS[s.model]} · {s.perspective} · {s.count} items</p>
          <ul className="mt-2 space-y-1.5">
            {s.latest.map((a) => <li key={a.id}><a href={safeUrl(a.link)} target="_blank" rel="noopener noreferrer" className="line-clamp-2 text-xs text-slate-700 hover:text-sky-900">{a.title}</a></li>)}
            {s.latest.length === 0 && <li className="text-xs text-slate-400">No recent items</li>}
          </ul>
        </div>
      ))}
    </div>
    <div className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500">
      <span className="font-medium text-slate-700">Volume balance {Math.round(p.balance)}/100</span> · {p.shared_stories.length ? `Shared stories: ${p.shared_stories.map((s) => s.label).slice(0, 2).join("; ")}` : "No shared stories in this window"}
    </div>
  </div>
);

export const CompareView = () => {
  const [coverage, setCoverage] = useState("both");
  const [stories, setStories] = useState(null);
  const [pairs, setPairs] = useState(null);
  useEffect(() => { setStories(null); fetchStories({ coverage, limit: 25 }).then(setStories).catch(() => setStories([])); }, [coverage]);
  useEffect(() => { fetchPairs(48).then(setPairs).catch(() => setPairs([])); }, []);
  return (
    <div className="space-y-12" data-testid="compare-view">
      <section>
        <h2 className="font-serif text-2xl text-slate-900">Side-by-side coverage</h2>
        <p className="mt-1 text-sm text-slate-500">Stories from the last 48 hours, grouped by AI across outlets. Open one to see headlines from each side and how the framing differs.</p>
        <div className="mt-4 flex flex-wrap gap-2" role="tablist">
          {COVERAGE.map(([v, l]) => (
            <button key={v} role="tab" aria-selected={coverage === v} onClick={() => setCoverage(v)} data-testid={`compare-coverage-${v}`}
              className={`rounded-full border px-4 py-1.5 text-sm ${coverage === v ? "border-sky-900 bg-sky-900 text-white" : "border-slate-200 bg-white text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-4 space-y-3">
          {!stories && [...Array(4)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-white" />)}
          {stories?.map((s) => <StoryRow key={s.id} s={s} />)}
          {stories?.length === 0 && <p className="py-8 text-center text-sm text-slate-500" data-testid="compare-empty">No stories in this group yet — grouping runs every 15 minutes.</p>}
        </div>
      </section>
      <section>
        <h2 className="font-serif text-2xl text-slate-900">Source pairings</h2>
        <p className="mt-1 text-sm text-slate-500">Each mainstream outlet is paired with an independent counterpart for balanced comparison (last 48 hours).</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">{pairs?.map((p) => <PairCard key={`${p.mainstream.id}-${p.independent.id}`} p={p} />)}</div>
      </section>
    </div>
  );
};
